'use client';

/**
 * ./src/modules/shopping-list/context/ShoppingListContext.tsx
 *
 * ShoppingListContext
 *
 * @copyright DesignShop, 2024
 * @since 08/13/2024
 */
import React, { createContext, useContext, useEffect, useState } from 'react';

import { useAffiliateCollectionBySlugQuery } from '@/modules/affiliate/gql/__generated/affiliate.generated';
import { useAppSelector } from '@/modules/common/hooks/useStore';
import { useCollectionCollectionsQuery } from '@/modules/product/gql/__generated/collections.generated';
import { useSkuSearch } from '@/modules/product/hooks/useSkuSearch';
import { ApiToCollectionSchema, Collection } from '@/modules/product/services/collectionTransform';

import { ShoppingListItem } from '../models/shoppingListItem.interface';
import { SavedLook, ShoppingListTextItem } from '../models/shoppingListTextItem.interface';
import { selectShoppingListTextItems, selectSortedShoppingList } from '../state/shoppingListSlice';

export type WishlistProduct = {
  type: 'product';
  item: ShoppingListItem;
};

export type WishlistLook = {
  type: 'look';
  item: SavedLook;
};

export type WishlistCollection = {
  type: 'collection';
  item: Collection;
};

export type MixedShoppingListItem = WishlistProduct | WishlistLook | WishlistCollection;

type ShoppingListContextType = {
  mixedShoppingListItems: MixedShoppingListItem[];
  isLoading: boolean;
};

const ShoppingListContext = createContext<ShoppingListContextType | undefined>(undefined);

export const ShoppingListProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const shoppingListInitialLoading = useAppSelector((state) => state.shoppingList.initialLoading);
  const sortedShoppingList = useAppSelector(selectSortedShoppingList);
  const shoppingListCollections = useAppSelector(selectShoppingListTextItems);

  const { products } = useSkuSearch(
    sortedShoppingList
      .filter(({ type, item }) => {
        return !!(type === 'product' && !!(item as ShoppingListItem).variant.sku);
      })
      .map(({ item }) => (item as ShoppingListItem).variant.sku)
  );

  const [mixedShoppingListItems, setMixedShoppingListItems] = useState<MixedShoppingListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const collectionSlugs = shoppingListCollections?.map((item) => item.key)?.filter(Boolean) || [];

  const { data: collectionsQuery, isFetching: isLoadingCollections } =
    useCollectionCollectionsQuery(
      { slug_in: collectionSlugs },
      {
        enabled: collectionSlugs.length > 0,
      }
    );

  const { data: affiliateData, isFetching: isLoadingAfiliateData } =
    useAffiliateCollectionBySlugQuery(
      { slug_in: collectionSlugs },
      {
        enabled: collectionSlugs.length > 0,
      }
    );

  useEffect(() => {
    if (shoppingListInitialLoading || isLoadingCollections) {
      return;
    }

    const parseMixedShoppingListItems = () => {
      const items: MixedShoppingListItem[] = sortedShoppingList
        .map(({ item, type }) => {
          if (type === 'product') {
            return {
              type: 'product',
              item: {
                ...item,
                published: products.find(
                  ({ sku }) => sku === (item as ShoppingListItem).variant.sku
                )?.published,
              },
            };
          } else if (type === 'look') {
            return { type: 'look', item: item };
          } else if (type === 'textItem') {
            const collectionItem = item as ShoppingListTextItem;
            const refCollection = collectionsQuery?.collectionCollection?.items.find(
              (collection) => collection?.slug === collectionItem.key
            );

            if (refCollection) {
              return {
                type: 'collection',
                item: ApiToCollectionSchema.parse(refCollection),
              };
            }
            const affiliateCollection = affiliateData?.affiliateCollection?.items.find(
              (collection) => collection?.slug === collectionItem.key
            );
            if (affiliateCollection) {
              return {
                type: 'collection',
                item: {
                  ...ApiToCollectionSchema.parse(affiliateCollection),
                  image: affiliateCollection?.newBaseImage?.url,
                  collectionType: 'affiliate',
                },
              };
            }
          }
          return null;
        })
        .filter((item): item is MixedShoppingListItem => item !== null);
      setMixedShoppingListItems(items);
      setIsLoading(false);
    };

    parseMixedShoppingListItems();
  }, [
    isLoadingCollections,
    collectionsQuery,
    sortedShoppingList,
    isLoadingAfiliateData,
    affiliateData,
    products,
    shoppingListInitialLoading,
  ]);

  return (
    <ShoppingListContext.Provider value={{ mixedShoppingListItems, isLoading }}>
      {children}
    </ShoppingListContext.Provider>
  );
};

export const useShoppingList = () => {
  const context = useContext(ShoppingListContext);
  if (!context) {
    throw new Error('useShoppingList must be used within a ShoppingListProvider');
  }
  return context;
};
